import { Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { api } from "./lib/api";
import { useAuth } from "./contexts/AuthContext";
import { WorldMap } from "./components/Map/WorldMap";
import { LevelPlayer } from "./components/Level/LevelPlayer";
import { RegisterScreen } from "./components/Auth/RegisterScreen";
import { LoginScreen } from "./components/Auth/LoginScreen";
import { ShopScreen } from "./components/Shop/ShopScreen";
import { MarketScreen } from "./components/Market/MarketScreen";
import { ProfileScreen } from "./components/Profile/ProfileScreen";
import { LeaderboardScreen } from "./components/Leaderboard/LeaderboardScreen";
import { LEVELS_BY_ID } from "./components/Level/levelData";
import { useGameStore } from "./store/gameStore";
import type { LevelCompletePayload } from "./components/Level/levelTypes";

// ─── Route guards ──────────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Redirect already-authenticated users away from /login and /register */
function GuestRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (user) return <Navigate to="/map" replace />;
  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#0f0e17",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#c8971a",
        fontSize: 22,
        fontFamily: "system-ui, sans-serif",
        letterSpacing: "0.05em",
      }}
    >
      Gollegiella…
    </div>
  );
}

// ─── Level page ────────────────────────────────────────────────────────────────

function LevelPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const setGold = useGameStore((s) => s.setGold);
  const addBadge = useGameStore((s) => s.addBadge);

  const level = id ? LEVELS_BY_ID[id] : undefined;

  if (!level) {
    return (
      <div style={{ color: "#f2ead8", padding: 32, fontFamily: "system-ui, sans-serif" }}>
        Brett «{id}» finnes ikke.{" "}
        <button
          style={{ color: "#c8971a", background: "none", border: "none", cursor: "pointer", fontSize: "inherit" }}
          onClick={() => navigate("/map")}
        >
          ← Tilbake til kartet
        </button>
      </div>
    );
  }

  async function handleComplete(payload: LevelCompletePayload) {
    if (!token) return;
    const res = await api.post<{ newGold: number; newBadges: string[] }>(
      `/level/${payload.levelId}/complete`,
      { stars: payload.stars, goldEarned: payload.goldEarned, wordsLearned: payload.wordsLearned },
      token
    );
    if (res.ok) {
      setGold(res.data.newGold);
      for (const b of res.data.newBadges) addBadge(b);
    }
  }

  return (
    <LevelPlayer
      level={level}
      onComplete={handleComplete}
      onExit={() => navigate("/map")}
    />
  );
}


// ─── Router ────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Routes>
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/map" replace />} />

      {/* Guest-only routes */}
      <Route path="/login"    element={<GuestRoute><LoginScreen /></GuestRoute>} />
      <Route path="/register" element={<GuestRoute><RegisterScreen /></GuestRoute>} />

      {/* Protected routes */}
      <Route path="/map"             element={<ProtectedRoute><WorldMap /></ProtectedRoute>} />
      <Route path="/level/:id"       element={<ProtectedRoute><LevelPage /></ProtectedRoute>} />
      <Route path="/shop"            element={<ProtectedRoute><ShopScreen /></ProtectedRoute>} />
      <Route path="/market"          element={<ProtectedRoute><MarketScreen /></ProtectedRoute>} />
      <Route path="/profile"         element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
      <Route path="/leaderboard"     element={<ProtectedRoute><LeaderboardScreen /></ProtectedRoute>} />
    </Routes>
  );
}
